import React, {useEffect, useMemo, useRef, useState} from 'react';
import ReactTable, {ReactTableDefaults} from 'react-table';
import 'react-table/react-table.css';
import PT from 'prop-types';
import cn from 'classnames';
import throttle from 'lodash/throttle';

import BodyCell from './components/BodyCell';
import Pagination from './components/Pagination';
import EmptyComponent from 'Components/EmptyComponent';
import useQueryParamsFilter from '../../../screens/shared/hooks/useQueryParamsFilter';
import TableComponent from 'Components/Admin/Table/components/TableComponent';

const Table = (props) => {
  const {
    columns: columnsProps = [],
    data,
    className,
    reactTableProps,
    getTableProps,
    sortable,
    watchForResetPage,
    watchForTabChange,
    stickyColumn,
    tableSettings: {hasHeader = true, hasBody = true, pageSize = 0, CustomRowComponent},
  } = props;

  const filterQueryParams = (query) => {
    if ('page' in query && query.page < 1) delete query.page;

    return query;
  };

  const [paginationData, setPaginationData, {isInitialized}] = useQueryParamsFilter({
    page: 0,
    pageSize,
  }, {filter: filterQueryParams});

  const setPagination = (data) => {
    setPaginationData((paginationData) => ({
      ...paginationData,
      ...data,
    }));
  };

  useEffect(() => {
    if (isInitialized) setPagination({page: 0, pageSize});
  }, [watchForTabChange]);

  useEffect(() => {
    if (watchForResetPage) setPagination({page: 0});
  }, [watchForResetPage]);

  const handlePageChange = (page) => setPagination({page});

  const handlePageSizeChange = (pageSize) => setPagination({page: 0, pageSize});

  const columns = useMemo(
    () =>
      columnsProps.map((el) => {
        const {title, cellProps} = el;
        return {
          Header: hasHeader ? title : EmptyComponent,
          Cell: hasBody ? (cellProps.Cell ? cellProps.Cell : BodyCell) : EmptyComponent,
          ...cellProps,
        };
      }),
    [columnsProps, hasHeader, hasBody]
  );

  const [isTableScroll, setIsTableScroll] = useState(false);

  const ref = useRef();

  const checkIsScroll = () => {
    const $table = ref.current.querySelector('.rt-table');

    return $table.scrollWidth > $table.offsetWidth + 1;
  };

  const handleResize = () => {
    if (ref.current) setIsTableScroll(checkIsScroll());
  };

  const handleResizeThrottle = throttle(handleResize, 250);

  useEffect(() => {
    if (ref.current) setIsTableScroll(checkIsScroll());

    window.addEventListener('resize', handleResizeThrottle);

    return () => {
      window.removeEventListener('resize', handleResizeThrottle);
    }
  }, []);

  const CurrentTableComponent = useMemo(() => {
    return stickyColumn
      ? (props) => (<TableComponent {...props} stickyColumn={stickyColumn} isTableScroll={isTableScroll}/>)
      : ReactTableDefaults.TableComponent
  }, [stickyColumn, isTableScroll, paginationData]);

  return (
    <div
      className={cn(
        'table',
        {
          'table-empty':
            data && data.length && data[0].not_found && data[0].not_found.title === 'No contacts with this name',
          'table_with-pagination': data ? data.length > pageSize : null,
          'table_with-scroll': isTableScroll
        },
        className
      )}
      ref={ref}
    >
      <ReactTable
        data={data}
        columns={columns}
        key={data}
        page={paginationData.page}
        defaultPage={paginationData.page}
        pageSize={paginationData.pageSize}
        defaultPageSize={paginationData.pageSize}
        pageSizeOptions={[10, 20, 40, 100]}
        getTableProps={getTableProps}
        PaginationComponent={Pagination}
        TheadComponent={hasHeader ? ReactTableDefaults.TheadComponent : EmptyComponent}
        TrGroupComponent={CustomRowComponent || ReactTableDefaults.TrGroupComponent}
        TableComponent={CurrentTableComponent}
        minRows={0}
        NoDataComponent={EmptyComponent}
        sortable={sortable}
        showPagination={!!data && data.length > pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        {...reactTableProps}
      />
    </div>
  );
};

Table.propTypes = {
  columns: PT.arrayOf(
    PT.shape({
      title: PT.oneOfType([PT.string, PT.number]),
      cellProps: PT.object,
    })
  ),
  data: PT.array,
  reactTableProps: PT.object,
  tableSettings: PT.object,
  watchForResetPage: PT.any,
  watchForTabChange: PT.any
};

export default Table;
